import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import PublicNavigation from '../../components/ui/PublicNavigation';
import ShipmentHeader from './components/ShipmentHeader';
import ShipmentDetailsCard from './components/ShipmentDetailsCard';
import ShipmentTimeline from './components/ShipmentTimeline';
import ContainerInfoCard from './components/ContainerInfoCard';
import PackagePhotoSection from './components/PackagePhotoSection';
import DocumentsSection from './components/DocumentsSection';
import ContactCard from './components/ContactCard';
import EmptyState from './components/EmptyState';
import LoyaltyCardSection from './components/LoyaltyCardSection';
import { shipmentService } from '../../services/shipmentService';
import Icon from '../../components/AppIcon';

const ShipmentTrackingTimeline = () => {
  const { reference } = useParams();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShipmentByReference = async () => {
      if (!reference) {
        setError('Aucune référence fournie');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await shipmentService?.getShipmentByReference(reference);
        
        if (!data) {
          setError('Aucun envoi trouvé avec cette référence');
        } else {
          setShipment(data);
        }
      } catch (err) {
        console.error('Error fetching shipment:', err);
        setError(err?.message || 'Erreur lors de la récupération de l\'envoi');
      } finally {
        setLoading(false);
      }
    };

    fetchShipmentByReference();
  }, [reference]);

  // 5-step progress tracking — mapped to real DB current_status values
  const trackingSteps = [
    { 
      id: 1, 
      label: 'Enlevé chez le client', 
      status: 'depose',
      icon: 'Home'
    },
    { 
      id: 2, 
      label: 'Réceptionné à l\'entrepôt', 
      status: 'en_preparation',
      icon: 'Package'
    },
    { 
      id: 3, 
      label: 'Chargé dans le conteneur', 
      status: 'en_transit',
      icon: 'Box'
    },
    { 
      id: 4, 
      label: 'En transit maritime', 
      status: 'arrive_abidjan',
      icon: 'Ship'
    },
    { 
      id: 5, 
      label: 'Arrivé à Abidjan', 
      status: 'livre',
      icon: 'MapPin'
    }
  ];

  // Status order for progression comparison
  const STATUS_ORDER = ['en_attente', 'depose', 'en_preparation', 'en_transit', 'arrive_abidjan', 'en_livraison', 'livre'];

  // Map container status to equivalent shipment status for display
  const CONTAINER_STATUS_TO_SHIPMENT_STATUS = {
    'AU_PORT_LE_HAVRE_ANVERS': 'en_transit',
    'EN_TRANSIT_MARITIME': 'en_transit',
    'ARRIVE_A_ABIDJAN': 'arrive_abidjan',
    'DEDOUANEMENT_EN_COURS': 'arrive_abidjan',
    'TRANSFERT_A_ENTREPOT': 'arrive_abidjan',
    'DISTRIBUTION_EN_COURS': 'en_livraison',
    'CONTENEUR_TERMINE': 'livre'
  };

  // Container status French labels for display
  const CONTAINER_STATUS_LABELS = {
    'AU_PORT_LE_HAVRE_ANVERS': 'En attente de chargement / au port de départ',
    'EN_TRANSIT_MARITIME': 'En transit maritime',
    'ARRIVE_A_ABIDJAN': 'Arrivé à Abidjan',
    'DEDOUANEMENT_EN_COURS': 'Dédouanement en cours',
    'TRANSFERT_A_ENTREPOT': 'Transfert vers entrepôt Abidjan',
    'DISTRIBUTION_EN_COURS': 'Distribution en cours',
    'CONTENEUR_TERMINE': 'Livraison / distribution terminée'
  };

  // Get the effective status: use container status if shipment is linked to a container
  // and the container has a more advanced status than the shipment's individual status
  const getEffectiveStatus = () => {
    const shipmentStatus = shipment?.status;
    const containerStatus = shipment?.container?.current_status || shipment?.container?.status;

    if (!containerStatus) return shipmentStatus;

    const containerEquivalent = CONTAINER_STATUS_TO_SHIPMENT_STATUS?.[containerStatus];
    if (!containerEquivalent) return shipmentStatus;

    const shipmentIdx = STATUS_ORDER?.indexOf(shipmentStatus);
    const containerIdx = STATUS_ORDER?.indexOf(containerEquivalent);

    // Use container status if it's more advanced than the shipment's individual status
    if (containerIdx > shipmentIdx) return containerEquivalent;
    return shipmentStatus;
  };

  const effectiveStatus = getEffectiveStatus();

  const getStepStatus = (stepStatus) => {
    if (!effectiveStatus) return 'upcoming';
    
    const currentIndex = STATUS_ORDER?.indexOf(effectiveStatus);
    const stepIndex = STATUS_ORDER?.indexOf(stepStatus);
    
    if (currentIndex === -1) return 'upcoming';
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  const getCurrentStepMessage = () => {
    const containerStatus = shipment?.container?.current_status || shipment?.container?.status;
    const container = shipment?.container;
    
    // If linked to a container, show container-based message
    if (containerStatus && CONTAINER_STATUS_LABELS?.[containerStatus]) {
      const containerCode = container?.container_code || container?.container_number || '';
      const label = CONTAINER_STATUS_LABELS?.[containerStatus];
      return `📍 ${containerCode ? `Conteneur ${containerCode} : ` : ''}${label}.`;
    }

    // Fallback to individual shipment status
    const status = shipment?.status;
    switch(status) {
      case 'en_attente':
        return '📍 Votre envoi est en attente de prise en charge.';
      case 'depose':
        return '📍 Votre envoi a été déposé et est en cours d\'acheminement vers notre entrepôt.';
      case 'en_preparation':
        return '📍 Votre envoi est réceptionné dans notre entrepôt et en cours de préparation.';
      case 'en_transit':
        return `📍 Votre envoi est chargé dans le conteneur ${container?.container_code || container?.container_number || ''}. En transit maritime.`;
      case 'arrive_abidjan':
        return '📍 Votre envoi est arrivé au port d\'Abidjan et est en cours de dédouanement.';
      case 'en_livraison':
        return '📍 Votre envoi est en cours de livraison à domicile.';
      case 'livre':
        return '✅ Votre envoi a été livré avec succès.';
      default:
        return '📍 Suivi en cours...';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicNavigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des informations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !shipment) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <EmptyState 
            message={error || 'Envoi introuvable'}
            onBack={() => navigate('/tracking')}
            reference={reference}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Suivi {reference} - ADT Express</title>
        <meta name="description" content={`Suivez votre envoi ${reference} en temps réel`} />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <PublicNavigation />
        
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ShipmentHeader 
            shipment={shipment}
            reference={shipment?.reference}
            status={shipment?.status}
          />

          {/* 5-Step Progress Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-lg p-8 mb-8"
          >
            {/* Shipment Info Header */}
            <div className="mb-8 pb-6 border-b border-gray-200">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Référence : </span>
                  <span className="font-bold text-gray-900">{shipment?.reference}</span>
                </div>
                <div className="h-4 w-px bg-gray-300" />
                <div>
                  <span className="text-gray-600">Type : </span>
                  <span className="font-semibold text-gray-900">
                    {shipment?.shipment_type === 'BARREL' ? 'Barrique' : 
                     shipment?.shipment_type === 'VOLUME' ? 'Volume' : 'Conteneur'}
                  </span>
                </div>
                {shipment?.container?.departure_date && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <div>
                      <span className="text-gray-600">Départ : </span>
                      <span className="font-semibold text-gray-900">
                        {new Date(shipment.container.departure_date)?.toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </>
                )}
                {shipment?.container?.estimated_arrival_date && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <div>
                      <span className="text-gray-600">Arrivée estimée : </span>
                      <span className="font-semibold text-gray-900">
                        {new Date(shipment.container.estimated_arrival_date)?.toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </>
                )}
                {shipment?.container?.vessel_name && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <div>
                      <span className="text-gray-600">Navire : </span>
                      <span className="font-semibold text-gray-900">{shipment?.container?.vessel_name}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="relative mb-8">
              {/* Connecting Line */}
              <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200" style={{ marginLeft: '2.5rem', marginRight: '2.5rem' }}>
                <div 
                  className="h-full bg-green-600 transition-all duration-500"
                  style={{ 
                    width: (() => {
                      const currentIdx = trackingSteps?.findIndex(s => getStepStatus(s?.status) === 'current');
                      const completedCount = trackingSteps?.filter(s => getStepStatus(s?.status) === 'completed')?.length;
                      if (currentIdx === -1 && completedCount === trackingSteps?.length) return '100%';
                      if (currentIdx === -1) return '0%';
                      return `${(currentIdx / (trackingSteps?.length - 1)) * 100}%`;
                    })()
                  }}
                />
              </div>

              {/* Steps */}
              <div className="relative grid grid-cols-5 gap-2">
                {trackingSteps?.map((step, index) => {
                  const status = getStepStatus(step?.status);
                  return (
                    <div key={step?.id} className="flex flex-col items-center">
                      {/* Circle */}
                      <div className="relative z-10 mb-3">
                        {status === 'completed' ? (
                          <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center shadow-lg">
                            <Icon name="Check" size={28} color="white" />
                          </div>
                        ) : status === 'current' ? (
                          <div className="relative">
                            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                              <Icon name={step?.icon} size={28} color="white" />
                            </div>
                            <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75" />
                          </div>
                        ) : (
                          <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                            <Icon name={step?.icon} size={28} color="white" />
                          </div>
                        )}
                      </div>
                      {/* Label */}
                      <p className={`text-xs sm:text-sm text-center font-medium ${
                        status === 'completed' || status === 'current' 
                          ? 'text-green-700' :'text-gray-500'
                      }`}>
                        {step?.label}
                      </p>
                      {/* Date (if completed) */}
                      {status === 'completed' && shipment?.created_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(shipment.created_at)?.toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Current Status Message */}
            <div className="bg-green-50 border-l-4 border-green-600 rounded-lg p-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                {getCurrentStepMessage()}
              </p>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <ShipmentDetailsCard 
                shipment={shipment}
                shipmentItems={shipment?.items || []}
                weight={shipment?.weight}
                dimensions={shipment?.dimensions}
              />
              <ShipmentTimeline 
                shipment={shipment}
                events={shipment?.tracking_events || []}
                currentStatus={shipment?.status}
              />
              <PackagePhotoSection shipment={shipment} files={shipment?.files || []} />
              <DocumentsSection 
                shipment={shipment}
                shipmentId={shipment?.id}
                files={shipment?.files || []}
              />
              <LoyaltyCardSection shipment={shipment} />
            </div>
            
            <div className="space-y-8">
              <ContainerInfoCard container={shipment?.container} />
              <ContactCard 
                type="sender"
                name={shipment?.sender_name}
                phone={shipment?.sender_phone}
                address={shipment?.sender_address}
                city={shipment?.sender_city}
                country="France"
              />
              <ContactCard 
                type="receiver"
                name={shipment?.receiver_name}
                phone={shipment?.receiver_phone}
                address={shipment?.receiver_address}
                city={shipment?.receiver_city}
                country={shipment?.receiver_country}
              />
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-green-900 text-white mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-bold mb-4">ADT Express</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Votre partenaire de confiance pour l'expédition de colis entre la France et la Côte d'Ivoire depuis plus de 10 ans.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Navigation</h3>
                <ul className="space-y-2 text-sm">
                  <li><a href="/" className="text-gray-300 hover:text-white transition-colors">Accueil</a></li>
                  <li><a href="/pricing" className="text-gray-300 hover:text-white transition-colors">Tarifs</a></li>
                  <li><a href="/tracking" className="text-gray-300 hover:text-white transition-colors">Suivi</a></li>
                  <li><a href="/blog" className="text-gray-300 hover:text-white transition-colors">Blog</a></li>
                  <li><a href="/shipping-information" className="text-gray-300 hover:text-white transition-colors">Qui sommes-nous</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Services</h3>
                <ul className="space-y-2 text-sm">
                  <li><a href="/client-login" className="text-gray-300 hover:text-white transition-colors">Espace Client</a></li>
                  <li><a href="/professional-services" className="text-gray-300 hover:text-white transition-colors">Espace Professionnel</a></li>
                  <li><span className="text-gray-300">Barriques Noire & Bleue</span></li>
                  <li><span className="text-gray-300">Cartons Triple Cannelure</span></li>
                  <li><span className="text-gray-300">Livraison à domicile</span></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Contact</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start space-x-2">
                    <Icon name="MapPin" size={16} className="flex-shrink-0 mt-1" color="white" />
                    <span className="text-gray-300">23 rue de l'essonne 91000 evry</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Phone" size={16} className="flex-shrink-0 mt-1" color="white" />
                    <a href="tel:0651988621" className="text-gray-300 hover:text-white transition-colors">06 51 98 86 21</a>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="MessageCircle" size={16} className="flex-shrink-0 mt-1" color="white" />
                    <a href="https://wa.me/33651988621" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">WhatsApp</a>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Mail" size={16} className="flex-shrink-0 mt-1" color="white" />
                    <span className="text-gray-300">contact@adtexpress.fr</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="border-t border-green-800 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <p className="text-sm text-gray-300">© 2026 ADT Express — Tous droits réservés</p>
                <div className="flex items-center space-x-6 text-sm">
                  <a href="/mentions-legales" className="text-gray-300 hover:text-white transition-colors">Mentions légales</a>
                  <a href="/cgv" className="text-gray-300 hover:text-white transition-colors">CGV</a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ShipmentTrackingTimeline;